import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { Plus, X, Ship, Search, Edit3, Trash2, Anchor, Plane, Truck, PackageOpen } from "lucide-react";
import { toast } from "sonner";
import { CountrySelect } from "@/components/GeoSelect";

const EMPTY = {
  nombre: "", modo: "maritimo",
  puerto_origen: "", puerto_destino: "",
  pais_origen: "", pais_destino: "",
  vessel_number: "", container_number: "",
  cut_off: "", salida: "", llegada: "",
  activa: true, notas: "", custom_fields: {},
};

const MODE_ICON = { aereo: Plane, terrestre: Truck, maritimo: Anchor };

export default function Voyages() {
  const { api, user } = useAuth();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [countries, setCountries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [q, setQ] = useState("");
  const [customPairs, setCustomPairs] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);

  const openDetail = async (v) => {
    setDetail(v);
    setDetailData(null);
    try {
      const { data } = await api.get(`/voyages/${v.id}/detail`);
      setDetailData(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const isAdmin = user.role === "admin";

  const load = () => api.get("/voyages").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/countries").then((r) => setCountries(r.data.filter(c => c.activo))).catch(() => {});
  }, []); // eslint-disable-line

  const openNew = () => { setForm(EMPTY); setCustomPairs([]); setEditingId(null); setShowForm(true); };
  const openEdit = (v) => {
    setForm({ ...EMPTY, ...v });
    setCustomPairs(Object.entries(v.custom_fields || {}).map(([k, val]) => ({ k, v: val })));
    setEditingId(v.id);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const custom_fields = {};
      customPairs.forEach(({ k, v }) => { if (k.trim()) custom_fields[k.trim()] = v || ""; });
      const payload = { ...form, custom_fields };
      if (editingId) await api.patch(`/voyages/${editingId}`, payload);
      else await api.post("/voyages", payload);
      toast.success(t("saved"));
      setShowForm(false); setEditingId(null); setForm(EMPTY); setCustomPairs([]);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/voyages/${id}`);
    load();
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.trim().toLowerCase();
    return rows.filter((r) => [r.codigo, r.nombre, r.puerto_origen, r.puerto_destino, r.vessel_number, r.container_number].filter(Boolean).some((v) => String(v).toLowerCase().includes(s)));
  }, [rows, q]);

  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100";
  const lbl = "text-[11px] font-semibold text-slate-600 uppercase mb-1 block";

  return (
    <div className="space-y-4" data-testid="voyages-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <Ship className="w-5 h-5 text-red-600" /> {t("voyages")}
          </h2>
          <p className="text-sm text-slate-500">{filtered.length} / {rows.length} · {isAdmin ? t("admin_editable") : t("read_only")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute start-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")+"..."} className="ps-9 pe-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600 w-72" />
          </div>
          {isAdmin && (
            <button onClick={openNew} data-testid="new-voyage-btn" className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2">
              <Plus className="w-4 h-4" /> {t("new_voyage")}
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="p-3 text-start text-[10px] uppercase tracking-wider font-semibold">{t("voyage")}</th>
                <th className="p-3 text-start text-[10px] uppercase tracking-wider font-semibold">{t("mode") || "Modo"}</th>
                <th className="p-3 text-start text-[10px] uppercase tracking-wider font-semibold">{t("port_from")} → {t("port_to")}</th>
                <th className="p-3 text-start text-[10px] uppercase tracking-wider font-semibold">{t("vessel")}</th>
                <th className="p-3 text-start text-[10px] uppercase tracking-wider font-semibold">{t("container")}</th>
                <th className="p-3 text-start text-[10px] uppercase tracking-wider font-semibold">{t("cut_off")}</th>
                <th className="p-3 text-start text-[10px] uppercase tracking-wider font-semibold">{t("departure")}</th>
                <th className="p-3 text-start text-[10px] uppercase tracking-wider font-semibold">{t("arrival")}</th>
                <th className="p-3 text-start text-[10px] uppercase tracking-wider font-semibold">{t("status")}</th>
                {<th className="p-3 text-end text-[10px] uppercase tracking-wider font-semibold">{t("actions")}</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((v) => {
                const Icon = MODE_ICON[v.modo] || Ship;
                return (
                  <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`voy-${v.codigo}`}>
                    <td className="p-3">
                      <div className="font-mono font-semibold text-slate-900 text-xs">{v.codigo}</div>
                      <div className="text-xs text-slate-600">{v.nombre}</div>
                    </td>
                    <td className="p-3"><Icon className="w-4 h-4 text-slate-500" /></td>
                    <td className="p-3 text-xs text-slate-700">
                      <div className="font-semibold">{v.puerto_origen}</div>
                      <div className="text-slate-500">→ {v.puerto_destino}</div>
                    </td>
                    <td className="p-3 text-slate-600 text-xs font-mono">{v.vessel_number || "—"}</td>
                    <td className="p-3 text-slate-600 text-xs font-mono">{v.container_number || "—"}</td>
                    <td className="p-3 text-slate-600 text-xs tabular-nums">{v.cut_off || "—"}</td>
                    <td className="p-3 text-slate-800 text-xs tabular-nums font-semibold">{v.salida || "—"}</td>
                    <td className="p-3 text-slate-600 text-xs tabular-nums">{v.llegada || "—"}</td>
                    <td className="p-3">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${v.activa ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {v.activa ? t("active") : t("inactive")}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="p-3">
                        <div className="flex items-center gap-1 justify-end">
                          <button onClick={() => openDetail(v)} data-testid={`voy-detail-${v.codigo}`} className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded" title={t("view_details")}><PackageOpen className="w-4 h-4" /></button>
                          <button onClick={() => openEdit(v)} data-testid={`edit-voy-${v.codigo}`} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"><Edit3 className="w-4 h-4" /></button>
                          <button onClick={() => remove(v.id)} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    )}
                    {!isAdmin && (
                      <td className="p-3 text-end">
                        <button onClick={() => openDetail(v)} data-testid={`voy-detail-${v.codigo}`} className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded" title={t("view_details")}><PackageOpen className="w-4 h-4" /></button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={isAdmin ? 10 : 9} className="p-8 text-center text-slate-400">
                  <Ship className="w-10 h-10 mx-auto opacity-30 mb-2" />{t("no_voyages")}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-3xl p-6 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="voyage-modal">
            <div className="flex justify-between items-center">
              <h3 className="font-display font-semibold text-lg">{editingId ? t("edit") : t("new_voyage")}</h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className={lbl}>{t("name")} *</label>
                <input required data-testid="vf-name" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Meos Sea Guangzhou → Barcelona" className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("mode") || "Modo"}</label>
                <select value={form.modo} onChange={(e) => setForm({ ...form, modo: e.target.value })} className={inp}>
                  <option value="maritimo">{t("sea") || "Marítimo"}</option>
                  <option value="aereo">{t("air") || "Aéreo"}</option>
                  <option value="terrestre">{t("ground") || "Terrestre"}</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.activa} onChange={(e) => setForm({ ...form, activa: e.target.checked })} /> {t("active")}</label>
              <div>
                <label className={lbl}>{t("origin_country")}</label>
                <CountrySelect countries={countries} value={form.pais_origen} onChange={(v) => setForm({ ...form, pais_origen: v })} className={inp} testid="vf-country-from" />
              </div>
              <div>
                <label className={lbl}>{t("destination_country")}</label>
                <CountrySelect countries={countries} value={form.pais_destino} onChange={(v) => setForm({ ...form, pais_destino: v })} className={inp} testid="vf-country-to" />
              </div>
              <div>
                <label className={lbl}>{t("port_from")} *</label>
                <input required data-testid="vf-port-from" value={form.puerto_origen} onChange={(e) => setForm({ ...form, puerto_origen: e.target.value })} placeholder="Guangzhou" className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("port_to")} *</label>
                <input required data-testid="vf-port-to" value={form.puerto_destino} onChange={(e) => setForm({ ...form, puerto_destino: e.target.value })} placeholder="Barcelona" className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("vessel")}</label>
                <input data-testid="vf-vessel" value={form.vessel_number} onChange={(e) => setForm({ ...form, vessel_number: e.target.value })} placeholder="MSC ANNA / voy 245W" className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("container_number")}</label>
                <input value={form.container_number} onChange={(e) => setForm({ ...form, container_number: e.target.value })} placeholder="MSKU1234567" className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("cut_off")}</label>
                <input type="date" data-testid="vf-cutoff" value={form.cut_off} onChange={(e) => setForm({ ...form, cut_off: e.target.value })} className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("departure")}</label>
                <input type="date" data-testid="vf-departure" value={form.salida} onChange={(e) => setForm({ ...form, salida: e.target.value })} className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("arrival")}</label>
                <input type="date" data-testid="vf-arrival" value={form.llegada} onChange={(e) => setForm({ ...form, llegada: e.target.value })} className={inp} />
              </div>
              <div className="sm:col-span-2">
                <label className={lbl}>{t("notes")}</label>
                <textarea rows="2" value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} className={inp} />
              </div>
            </div>

            {/* Custom fields */}
            <div className="border border-slate-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("custom_fields")}</span>
                <button type="button" onClick={() => setCustomPairs([...customPairs, { _uid: `p-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, k: "", v: "" }])} data-testid="vf-add-field" className="text-xs font-semibold text-red-600 hover:underline">+ {t("add")}</button>
              </div>
              {customPairs.map((p, i) => (
                <div key={p._uid || `pair-${i}`} className="grid grid-cols-2 gap-2 mb-2">
                  <input value={p.k} onChange={(e) => setCustomPairs(customPairs.map((x, j) => j === i ? { ...x, k: e.target.value } : x))} placeholder={t("field_name") || "Field"} className={inp} />
                  <div className="flex gap-2">
                    <input value={p.v} onChange={(e) => setCustomPairs(customPairs.map((x, j) => j === i ? { ...x, v: e.target.value } : x))} placeholder={t("value") || "Value"} className={inp + " flex-1"} />
                    <button type="button" onClick={() => setCustomPairs(customPairs.filter((_, j) => j !== i))} className="p-2 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button>
              <button type="submit" data-testid="vf-submit" className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save")}</button>
            </div>
          </form>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="voyage-detail-modal">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  {React.createElement(MODE_ICON[detail.modo] || Ship, { className: "w-5 h-5 text-blue-600" })}
                  <h3 className="font-display font-semibold text-lg">{detail.codigo}</h3>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${detail.activa ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{detail.activa ? t("active") : t("inactive")}</span>
                </div>
                <div className="text-sm text-slate-600">{detail.nombre} · {detail.puerto_origen} → {detail.puerto_destino}</div>
              </div>
              <button onClick={() => { setDetail(null); setDetailData(null); }}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="p-4 space-y-4">
              {/* Voyage meta */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <MetaCell label={t("vessel")} value={detail.vessel_number} />
                <MetaCell label={t("container")} value={detail.container_number} />
                <MetaCell label={t("cut_off")} value={detail.cut_off} />
                <MetaCell label={t("departure")} value={detail.salida} />
                <MetaCell label={t("arrival")} value={detail.llegada} />
              </div>

              {!detailData ? (
                <div className="p-8 text-center text-slate-400 text-sm">…</div>
              ) : (
                <>
                  {/* Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <StatBox label={t("manifests")} value={detailData.stats.manifests_count} color="blue" />
                    <StatBox label={t("shipments")} value={detailData.stats.shipments_count} color="emerald" />
                    <StatBox label={t("weight_kg")} value={detailData.stats.total_peso.toFixed(2)} color="amber" />
                    <StatBox label="CBM" value={detailData.stats.total_cbm.toFixed(3)} color="purple" />
                  </div>

                  {/* Manifests */}
                  <div>
                    <h4 className="text-xs font-bold uppercase text-slate-600 tracking-wider mb-2">{t("manifests")} ({detailData.manifests.length})</h4>
                    <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-56 overflow-y-auto">
                      {detailData.manifests.length === 0 && <div className="p-3 text-xs text-slate-400 italic">—</div>}
                      {detailData.manifests.map(m => (
                        <div key={m.id} className="p-2 flex flex-wrap items-center gap-2 text-xs" data-testid={`vd-mani-${m.codigo}`}>
                          <span className="font-mono font-semibold">{m.codigo}</span>
                          <span className="text-slate-500">{m.nombre}</span>
                          <span className="ms-auto text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-600">{m.estado}</span>
                          <span className="text-[10px] text-slate-400 tabular-nums">{new Date(m.created_at).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Shipments */}
                  <div>
                    <h4 className="text-xs font-bold uppercase text-slate-600 tracking-wider mb-2">{t("shipments")} ({detailData.shipments.length})</h4>
                    <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-64 overflow-y-auto">
                      {detailData.shipments.length === 0 && <div className="p-3 text-xs text-slate-400 italic">—</div>}
                      {detailData.shipments.map(s => (
                        <div key={s.id} className="p-2 flex flex-wrap items-center gap-2 text-xs" data-testid={`vd-ship-${s.tracking_code}`}>
                          <span className="font-mono font-semibold">{s.tracking_code}</span>
                          <span className="text-slate-500 flex-1 truncate">{s.descripcion || `${s.origen} → ${s.destino}`}</span>
                          <span className="tabular-nums text-slate-600">{(s.peso || 0)}kg</span>
                          <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">{s.estado}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetaCell({ label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
      <div className="text-[10px] uppercase font-semibold text-slate-500">{label}</div>
      <div className="font-mono text-slate-800 truncate">{value || "—"}</div>
    </div>
  );
}

function StatBox({ label, value, color }) {
  const colors = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
  };
  return (
    <div className={`rounded-lg p-3 border ${colors[color]}`}>
      <div className="text-[10px] uppercase font-semibold opacity-80">{label}</div>
      <div className="text-xl font-display font-bold tabular-nums">{value}</div>
    </div>
  );
}
